import type { Score } from '../model/types.js';
import {
  formatTiebreak,
  parseDescriptor,
  rankParticipants,
  tiebreakContext,
} from '../tiebreaks/index.js';
import type { Tournament } from '../tournament/types.js';

export interface TiebreakValue {
  readonly code: string;
  readonly value: number;
  readonly display: string;
}

export interface StandingRow {
  readonly rank: number;
  /** "1", "2-3" for shared places. */
  readonly rankLabel: string;
  readonly pairingNumber: number;
  readonly id: string;
  readonly name: string;
  readonly rating: number;
  readonly score: Score;
  readonly tiebreaks: readonly TiebreakValue[];
}

export interface StandingsOptions {
  readonly upToRound?: number | undefined;
  /** Override the tournament's tiebreak list (MTB26 descriptors). */
  readonly tiebreaks?: readonly string[];
}

/**
 * Standings after a round: points, then the tiebreaks in order, each applied
 * to the participants still tied (FIDE C.07 4.2). Equal on everything means
 * a shared place.
 */
export function computeStandings(t: Tournament, options: StandingsOptions = {}): StandingRow[] {
  const ctx = tiebreakContext(t, options.upToRound);
  const descriptors = (options.tiebreaks ?? t.settings.tiebreaks).map(parseDescriptor);
  const ranked = rankParticipants(ctx, descriptors);
  const byNumber = new Map(t.players.map((p) => [p.pairingNumber, p]));
  const size = new Map<number, number>();
  for (const r of ranked) size.set(r.rank, (size.get(r.rank) ?? 0) + 1);
  return ranked.map((r) => {
    const p = byNumber.get(r.id);
    const n = size.get(r.rank) as number;
    return {
      rank: r.rank,
      rankLabel: n === 1 ? String(r.rank) : `${r.rank}-${r.rank + n - 1}`,
      pairingNumber: r.id,
      id: p?.id ?? String(r.id),
      name: p?.name ?? '',
      rating: p?.rating ?? 0,
      score: ctx.entrants.get(r.id)?.score ?? 0,
      tiebreaks: descriptors.map((d, i) => ({
        code: d.text,
        value: r.values[i] ?? 0,
        display: formatTiebreak(d, r.values[i] ?? 0),
      })),
    };
  });
}
